(function ($) {
    /**
     * @ngdoc directive
     * @name updatePanel
     * @restrict EA
     *
     * @description
     * Fetches, compiles and includes HTML content.  Handles any form submits as AJAX postback.
     *
     * By default, the content URL is restricted to the same domain and protocol as the
     * application document. This is done by calling {@link ng.$sce#getTrustedResourceUrl
     * $sce.getTrustedResourceUrl} on it. To load content from other domains or protocols
     * you may either {@link ng.$sceDelegateProvider#resourceUrlWhitelist whitelist them} or
     * [wrap them](ng.$sce#trustAsResourceUrl) as trusted values. Refer to Angular's {@link
     * ng.$sce Strict Contextual Escaping}.
     *
     * In addition, the browser's
     * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
     * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
     * policy may further restrict whether the content is successfully loaded.
     * For example, `update-panel` won't work for cross-domain requests on all browsers and for `file://`
     * access on some browsers.
     *
     * @animations
     * enter - animation is used to bring new content into the browser.
     * leave - animation is used to animate existing content away.
     *
     * The enter and leave animation occur concurrently.
     *
     * @param {string} updatePanel|src content URL. 
     * @param {string=} onload Expression to evaluate when a new partial is loaded.
     *
     * @param {string=} autoscroll Whether `uploadPanel` should call {@link ng.$anchorScroll
     *                  $anchorScroll} to scroll the viewport after the content is loaded.
     *
     *                  - If the attribute is not set, disable scrolling.
     *                  - If the attribute is set without value, enable scrolling.
     *                  - Otherwise enable scrolling only if the expression evaluates to truthy value.
     * 
     * @methods
     * refresh() - updates the content via GET
     * update({url}) - posts data to the server and updates the content.  If url is not specified, 
     * updatePanel uses the src attribute.
     */

    /**
     * @ngdoc event
     * @name updatePanel#$contentRequested
     * @eventType emit on the scope updatePanel was declared in
     * @description
     * Emitted every time the updatePanel content is requested.
     */

    /**
     * @ngdoc event
     * @name updatePanel#$contentLoaded
     * @eventType emit on the current updatePanel scope
     * @description
     * Emitted every time the updatePanel content is reloaded.
     */
    angular.module('components.updatePanel', [])
        .directive('updatePanel', ['$http', '$templateCache', '$anchorScroll', '$animate', '$sce',
            function updatePanelFactory($http, $templateCache, $anchorScroll, $animate, $sce) {
                return {
                    restrict: 'EA',
                    priority: 400,
                    terminal: true,
                    transclude: 'element',
                    controller: angular.noop,
                    compile: function(element, attr) {

                        var srcExp = attr.updatePanel || attr.src,
                            onloadExp = attr.onload || '',
                            autoScrollExp = attr.autoscroll;

                        return function(scope, $element, $attr, ctrl, $transclude) {
                            var changeCounter = 0,
                                thisChangeId = 0,
                                currentScope,
                                previousElement,
                                currentElement;

                            var cleanupLastIncludeContent = function() {
                                if (previousElement) {
                                    previousElement.remove();
                                    previousElement = null;
                                }
                                if (currentScope) {
                                    currentScope.$destroy();
                                    currentScope = null;
                                }
                                if (currentElement) {
                                    $animate.leave(currentElement, function() {
                                        previousElement = null;
                                    });
                                    previousElement = currentElement;
                                    currentElement = null;
                                }
                            };

                            var updateContent = function(content) {
                                if (thisChangeId !== changeCounter) return;
                                var newScope = scope.$new();
                                ctrl.template = content;

                                // Note: This will also link all children that were contained in the original
                                // html. If that content contains controllers, ... they could pollute/change the scope.
                                // However, using update-panel on an element with additional content does not make sense...
                                // Note: We can't remove them in the cloneAttchFn of $transclude as that
                                // function is called before linking the content, which would apply child
                                // directives to non existing elements.
                                var clone = $transclude(newScope, function (clone) {
                                    cleanupLastIncludeContent();
                                    $animate.enter(clone, null, $element, afterAnimation);
                                });

                                currentScope = newScope;
                                currentElement = clone;

                                currentScope.refresh = doRefresh;
                                currentScope.update = doUpdate;
                                currentScope.$emit('$contentLoaded');
                                scope.$eval(onloadExp);
                            };

                            var errorHandler = function (response) {
                                if (thisChangeId === changeCounter) cleanupLastIncludeContent();
                                updateContent(response);
                            }

                            var afterAnimation = function () {
                                if (angular.isDefined(autoScrollExp) && (!autoScrollExp || scope.$eval(autoScrollExp))) {
                                    $anchorScroll();
                                }
                            };

                            function doRefresh(params) {
                                thisChangeId = ++changeCounter;

                                params = params || {};
                                //add cachebuster for IE8
                                params['_cb'] = '_' + new Date().getTime();

                                $http({
                                    method: 'GET',
                                    url: srcExp,
                                    params: params,
                                    cache: false
                                }).success(updateContent)
                                  .error(errorHandler);
                            }

                            function doUpdate(url, data) {
                                if (typeof url !== "string") {
                                    data = url;
                                    url = undefined;
                                }

                                thisChangeId = ++changeCounter;
                                var isJSON = typeof data === "object";

                                $http({
                                        method: 'POST',
                                        url: url || srcExp,
                                        data: data,
                                        headers: isJSON ? undefined : { 'Content-Type': 'application/x-www-form-urlencoded' },
                                        cache: false
                                    }).success(updateContent)
                                    .error(errorHandler);
                            }

                            doRefresh();
                        };
                    }
                };
            }
        ])

        // This directive is called during the $transclude call of the first `updatePanel` directive.
        // It will replace and compile the content of the element with the loaded template.
        // We need this directive so that the element content is already filled when
        // the link function of another directive on the same element as updatePanel
        // is called.
        .directive('updatePanel', ['$compile', '$timeout', function ($compile, $timeout) {
            return {
                restrict: 'EA',
                priority: -400,
                require: ['updatePanel', '?form'],
                link: function(scope, $element, $attr, ctrls) {
                    var ctrl = ctrls[0],
                        formCtrl = ctrls[1],
                        postbackUrl = $attr.action || $attr.updatePanel || $attr.src;

                    var ajaxFormHandler = function (event) {
                        if (formCtrl.$valid) {
                            var formData = $element.serialize();

                            //include button value if clicked (jQuery doesn't include this)
                            if (jQuery.contains($element[0], document.activeElement)) {
                                var eventBtn = document.activeElement;
                                formData += (formData.length > 1 ? '&' : '') + eventBtn.name + '=' + eventBtn.value;
                            }

                            scope.update(postbackUrl, formData);
                        }

                        event.preventDefault
                          ? event.preventDefault()
                          : event.returnValue = false; // IE
                    };

                    if (formCtrl) {
                        $element.on('submit', ajaxFormHandler);

                        // unregister the preventDefault listener so that we don't not leak memory but in a
                        // way that will achieve the prevention of the default action.
                        $element.on('$destroy', function () {
                            $timeout(function () {
                                $element.off('submit', ajaxFormHandler);
                            }, 0, false);
                        });
                    }
                
                    $element.html(ctrl.template);
                    $compile($element.contents())(scope);
                }
            };
        }]);

    if ($.browser.msie) {
        document.createElement('updatePanel'); //to make IE8 happy
    }

}(jQuery));